import { ApiError } from '@/lib/api-client';
import { authStore } from '@/stores/auth-store';
import type { ApiProblemBody } from '@/types/api';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/**
 * Downloads a file from an authenticated endpoint.
 *
 * A plain `<a href>` cannot carry the bearer token, so the bytes are fetched, wrapped
 * in an object URL, and handed to a synthetic link. The object URL is revoked
 * afterwards — leaving them alive pins the whole blob in memory for the session.
 */
export async function downloadAuthenticatedFile(path: string, fallbackFilename: string): Promise<void> {
  const token = authStore.getState().accessToken;

  const response = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'same-origin',
  });

  if (!response.ok) {
    let problem: ApiProblemBody;
    try {
      problem = (await response.json()) as ApiProblemBody;
    } catch {
      problem = {
        type: 'about:blank',
        title: 'Download failed',
        status: response.status,
        code: 'DOWNLOAD_FAILED',
        detail: 'The file could not be downloaded.',
      };
    }
    throw new ApiError(response.status, problem);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filenameFrom(response.headers.get('Content-Disposition')) ?? fallbackFilename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/** Reads the filename the server suggested, if any. */
function filenameFrom(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;

  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1] ?? null;
}
